import { useEffect, useState } from 'react';
import { useSellerAnalysisConfig, type SellerAnalysisConfig } from '../../hooks/useSellerAnalysis';
import { Advanced, RuleField, SaveStatus, SettingsSection, Switch, useAutoSave } from './settingsUi';

// Aba "Regras da IA (anuncios)": o que conta como problema para o diagnostico.
// A rotina automatica (ligar/frequencia) mora em "Rotinas de anuncios".

const DEFAULTS: SellerAnalysisConfig = {
  acosMax: 30,
  tacosMax: 15,
  ctrMin: null,
  cvrMin: null,
  ratingMin: 4.5,
  cooldownDays: 7,
  analysesUntilCut: 3,
  imageAnalysisEnabled: true,
  autoAnalysis: false,
  autoAnalysisIntervalHours: 24,
};

export function SellerAnalysisSettingsView() {
  const { config, loading, saveConfig } = useSellerAnalysisConfig();
  const [form, setForm] = useState<SellerAnalysisConfig | null>(null);
  const { status, schedule } = useAutoSave<SellerAnalysisConfig>(saveConfig);

  useEffect(() => {
    if (config && !form) setForm({ ...DEFAULTS, ...config });
  }, [config, form]);

  if (!form) return <p className="text-sm text-gray-500">{loading ? 'Carregando...' : 'Não foi possível carregar.'}</p>;

  const set = <K extends keyof SellerAnalysisConfig>(key: K, value: SellerAnalysisConfig[K], immediate = false) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    schedule({ [key]: value } as Partial<SellerAnalysisConfig>, immediate);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">A IA usa estes limites para decidir se um anúncio tem problema.</p>
        <SaveStatus status={status} />
      </div>

      <SettingsSection title="Quanto você aceita gastar" help="Acima disso, o anúncio vai para 'Para revisar' por mídia cara.">
        <RuleField
          label="Gasto máximo em Ads para cada R$ 100 vendidos pelo anúncio"
          tech="ACOS"
          suffix="%"
          value={form.acosMax}
          defaultValue={DEFAULTS.acosMax}
          onChange={(v) => set('acosMax', v ?? 0)}
          help={`Ex.: ${form.acosMax}% = até R$ ${form.acosMax} de Ads para cada R$ 100 que o Ads trouxe.`}
        />
        <RuleField
          label="Custo máximo de marketing sobre tudo que o anúncio vendeu"
          tech="TACOS"
          suffix="%"
          value={form.tacosMax}
          defaultValue={DEFAULTS.tacosMax}
          onChange={(v) => set('tacosMax', v ?? 0)}
          help="Soma Ads + comissão de afiliados e divide pela receita total do anúncio (orgânica + paga)."
        />
      </SettingsSection>

      <SettingsSection title="Qualidade do anúncio">
        <RuleField
          label="Nota mínima de avaliação"
          suffix="estrelas"
          value={form.ratingMin}
          defaultValue={DEFAULTS.ratingMin}
          onChange={(v) => set('ratingMin', v ?? 0)}
          help="Abaixo disso, a reputação entra como possível causa de venda baixa."
        />
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-gray-800">Analisar as fotos também</p>
            <p className="mt-0.5 text-[11px] text-gray-500">Quando o anúncio tem poucos cliques ou converte mal, a IA olha a foto principal e sugere melhorias.</p>
          </div>
          <Switch checked={form.imageAnalysisEnabled} onChange={(v) => set('imageAnalysisEnabled', v, true)} label="Analisar fotos" />
        </div>
      </SettingsSection>

      <Advanced help="Só mexa se souber o que está fazendo. Vazio = a IA usa a média da sua própria conta.">
        <RuleField
          label="Mínimo de cliques a cada 100 vezes que o anúncio aparece"
          tech="CTR"
          suffix="%"
          value={form.ctrMin}
          defaultValue={null}
          allowEmpty
          emptyLabel="média da conta"
          onChange={(v) => set('ctrMin', v)}
        />
        <RuleField
          label="Mínimo de vendas a cada 100 visitas"
          tech="Conversão"
          suffix="%"
          value={form.cvrMin}
          defaultValue={null}
          allowEmpty
          emptyLabel="média da conta"
          onChange={(v) => set('cvrMin', v)}
        />
        <RuleField
          label="Esperar depois de 'Corrigi' antes de analisar de novo"
          suffix="dias"
          integer
          value={form.cooldownDays}
          defaultValue={DEFAULTS.cooldownDays}
          onChange={(v) => set('cooldownDays', v ?? 0)}
          help="Tempo para a mudança fazer efeito antes da IA julgar outra vez."
        />
        <RuleField
          label="Depois de quantas correções sem melhora sugerir pausar o anúncio"
          integer
          value={form.analysesUntilCut}
          defaultValue={DEFAULTS.analysesUntilCut}
          onChange={(v) => set('analysesUntilCut', v ?? 3)}
        />
      </Advanced>
    </div>
  );
}
